import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";
import { getAnalytics, isSupported as analyticsIsSupported } from "firebase/analytics";
import API_URL from "../Config";
import { getStableDeviceId } from "../FingerPrint";

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
	  storageBucket: process.env.REACT_APP_FIREBASE_PROJECT_ID + ".firebasestorage.app",
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize the Firebase APP eagerly — initializeApp() is safe on every
// browser. Messaging and Analytics are initialized LAZILY and guarded with
// isSupported(), because getMessaging()/getAnalytics() can THROW at call time
// on unsupported environments (iOS Safari non-standalone, private mode, some
// in-app webviews). Calling them at module top level would crash this import
// and could take the whole app down with it.
export const app = initializeApp(firebaseConfig);

// Tracks whether the FCM token is CONFIRMED registered with our backend — not
// just whether the browser granted permission. The status chip uses this so it
// can't claim "Alerts on" when the token never reached the server (network
// error, SW delay, etc.). Persisted in localStorage so it survives reloads,
// and broadcast via an event so any mounted component can react live.
export const FCM_REGISTRATION_EVENT = "fcm-registration-changed";
const FCM_REGISTERED_KEY = "fcm_registered";

export const isFcmRegistered = () =>
  localStorage.getItem(FCM_REGISTERED_KEY) === "true";

const markFcmRegistered = (registered) => {
  localStorage.setItem(FCM_REGISTERED_KEY, registered ? "true" : "false");
  try {
    window.dispatchEvent(
      new CustomEvent(FCM_REGISTRATION_EVENT, { detail: { registered } }),
    );
  } catch (err) {
    // Non-critical: status chip will still self-correct on next focus/recheck.
  }
};

let messagingInstancePromise = null;
const MESSAGING_DATABASE_NAME = "firebase-messaging-database";
const FCM_RECOVERY_KEY = "fcm_version_recovery_attempted";

const deleteIndexedDatabase = (databaseName) =>
  new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(databaseName);
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
    request.onblocked = () =>
      reject(
        new Error(
          `Deletion of ${databaseName} is blocked; close other tabs for this site.`,
        ),
      );
  });

const recoverFromMessagingVersionError = async (registration) => {
  // A different Firebase SDK previously created this IndexedDB database at a
  // newer version. Firebase Messaging cannot downgrade it, so remove only its
  // disposable token cache and let the current SDK recreate it after reload.
  const subscription = await registration?.pushManager?.getSubscription();
  if (subscription) await subscription.unsubscribe();
  if (registration) await registration.unregister();

  await deleteIndexedDatabase(MESSAGING_DATABASE_NAME);
  localStorage.removeItem("fcm_token");
  markFcmRegistered(false);
};

/**
 * Lazily initialize Firebase Messaging behind a browser-support check.
 *
 * Cached after the first call. Never throws: returns the messaging instance on
 * supported browsers, or null (with a clear log) on unsupported ones so callers
 * can simply skip push setup.
 *
 * @returns {Promise<import("firebase/messaging").Messaging | null>}
 */
export const getMessagingInstance = () => {
  if (!messagingInstancePromise) {
    messagingInstancePromise = (async () => {
      try {
        const supported = await isSupported();
        if (!supported) {
          console.warn(
            "[FCM] Messaging not supported in this browser; skipping push setup."
          );
          return null;
        }
        const instance = getMessaging(app);
        console.info("[FCM] Messaging initialized.");
        return instance;
      } catch (err) {
        console.warn(
          "[FCM] Messaging initialization failed; skipping push setup.",
          err
        );
        return null;
      }
    })();
  }
  return messagingInstancePromise;
};

// Analytics is optional and unused by the notification flow. Initialize it
// lazily behind its own support check (fire-and-forget) so it can never throw
// at import time on unsupported browsers.
(async () => {
  try {
    if (await analyticsIsSupported()) {
      getAnalytics(app);
    }
  } catch (err) {
    // Non-critical: never let Analytics affect app load or push setup.
    console.warn("[FCM] Analytics unavailable; continuing without it.", err);
  }
})();

export const onMessageListener = () =>
  new Promise(async (resolve) => {
    const messaging = await getMessagingInstance();
    if (!messaging) {
      // Unsupported browser — no foreground listener to attach.
      resolve(null);
      return;
    }

    onMessage(messaging, (payload) => {
      console.log("[Firebase Message] Foreground message received:", payload);

      const { title, body } = payload.notification || {};

      if (Notification.permission === "granted") {
        window.dispatchEvent(
          new CustomEvent("fcm-message", { detail: payload })
        );
      }

      resolve(payload);
    });
  });

export const requestFirebaseNotificationPermission = async () => {
  try {
    if (!("Notification" in window)) {
      console.log("Browser doesn't support notifications");
      return null;
    }

    // Resolve messaging support up front. On unsupported browsers this returns
    // null (already logged) and we skip SW registration / token generation.
    const messaging = await getMessagingInstance();
    if (!messaging) {
      return null;
    }

    // 🔥 REGISTER SERVICE WORKER FIRST
     await navigator.serviceWorker.register(
      "/firebase-messaging-sw.js"
    );
    const registration = await navigator.serviceWorker.ready;
    console.log("Service Worker registered:", registration);

    const permission =
      Notification.permission === "granted"
        ? "granted"
        : await Notification.requestPermission();

    if (permission !== "granted") {
      console.log("Permission not granted");
      return null;
    }
    
    // 🔥 PASS REGISTRATION EXPLICITLY
    const vapidKeyForToken = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    const token = await getToken(messaging, {
      vapidKey: vapidKeyForToken, // your FULL public VAPID key
      serviceWorkerRegistration: registration,
    });

    // console.log("FCM Token:", token);
    return token;

  } catch (err) {
    console.error("FCM error:", err);

    if (
      err?.name === "VersionError" &&
      sessionStorage.getItem(FCM_RECOVERY_KEY) !== "true"
    ) {
      sessionStorage.setItem(FCM_RECOVERY_KEY, "true");
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        await recoverFromMessagingVersionError(registration);
        console.info(
          "[FCM] Removed incompatible messaging storage; reloading to retry.",
        );
        window.location.reload();
      } catch (recoveryError) {
        sessionStorage.removeItem(FCM_RECOVERY_KEY);
        console.error("[FCM] Automatic VersionError recovery failed:", recoveryError);
      }
    }
    return null;
  }
};

/**
 * Reusable, role-agnostic FCM registration.
 *
 * Requests permission / a token (browser-support + permission handling lives in
 * requestFirebaseNotificationPermission) and registers it with the backend so
 * the token can be created OR reactivated. Always calls /api/fcm/register when a
 * token is available — it never skips based on a cached localStorage token, so an
 * inactive token can self-heal. localStorage is written as a cache/debug value
 * only. Safe to call for any authenticated user; returns the token on success or
 * null on any failure (unsupported browser, denied permission, network error).
 *
 * @param {string} authKey Bearer auth token for the backend.
 * @returns {Promise<string|null>}
 */
export const registerFcmToken = async (authKey) => {
  try {
    if (!authKey) {
      console.warn("[FCM] No auth key provided; skipping registration.");
      markFcmRegistered(false);
      return null;
    }

    const token = await requestFirebaseNotificationPermission();
    if (!token) {
      // Unsupported browser, denied permission, or token generation failed.
      // requestFirebaseNotificationPermission already logged the reason.
      console.warn(
        "[FCM] No token available (unsupported/denied/failed); skipping registration."
      );
      markFcmRegistered(false);
      return null;
    }

    const deviceId = await getStableDeviceId();
    const response = await fetch(`${API_URL}/api/fcm/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authKey}`,
      },
      body: JSON.stringify({
        token,
        platform: "web",
        device_id: deviceId,
      }),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || "Failed to register token");
    }

    // Cache the latest token for debugging only — never used to gate registration.
    localStorage.setItem("fcm_token", token);
    markFcmRegistered(true);
    sessionStorage.removeItem(FCM_RECOVERY_KEY);
    console.info("[FCM] Token registered/reactivated with backend.");
    return token;
  } catch (err) {
    console.error("[FCM] Register error:", err);
    markFcmRegistered(false);
    return null;
  }
};







export default app;
